import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter as Router } from 'react-router-dom';
import { useEffect, type ReactNode } from 'react';
import { Spinner, Toaster } from '@/components/ui';
import { Shell } from '@/components/layout/Shell';
import { useApplyTheme } from '@/hooks/useApplyTheme';
import { useCurrentUser, useSession } from '@/hooks/useAuth';
import { documentTitle } from '@/lib/brand';
import { queryClient } from '@/lib/queryClient';
import { SignInPage } from '@/pages/SignInPage';
import { AppRoutes } from '@/routes';

/**
 * The session gate.
 *
 * `me` is the single source of truth for whether anyone is signed in: the shell
 * renders only while that query holds a user. Signing out therefore has to make
 * the query stop holding one, not merely hide the button.
 */
function Authenticated(): ReactNode {
  const me = useCurrentUser();
  const session = useSession();

  if (me.isPending) return <Spinner />;
  if (me.isError) return <SignInPage onSignedIn={session.signedIn} />;
  return (
    <Shell onSignedOut={session.signedOut}>
      <AppRoutes />
    </Shell>
  );
}

export function App(): ReactNode {
  useApplyTheme();

  // The tab title comes from the brand config, so renaming the product does not
  // leave the old name sitting in index.html.
  useEffect(() => {
    document.title = documentTitle();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <Authenticated />
      </Router>
      <Toaster />
    </QueryClientProvider>
  );
}
